import { zodResolver } from '@hookform/resolvers/zod'
import { ClipboardCheck, Globe, Users } from 'lucide-react'
import { useMemo, type ReactNode } from 'react'
import { useForm, type UseFormReturn } from 'react-hook-form'
import { z } from 'zod'
import { DepartmentPicker } from '../departments/DepartmentPicker'
import { FieldLabel, FormRequiredLegend } from '../ui/field-label'
import { Input } from '../ui/input'
import { MultiSelectPicker } from '../ui/multi-select-picker'
import { SelectPicker, type SelectOption } from '../ui/select-picker'
import type {
  Case,
  CaseFiltersResponse,
  CaseFormPayload,
  CasePhase,
  Gender,
} from '../../types/case'
import { CASE_PHASE_FIELD_LABELS, CASE_PHASE_FILTERS } from '../../types/case'
import type { Department } from '../../types/department'

const fieldTextareaClassName =
  'min-h-28 w-full rounded-md border border-parch-200 bg-white px-3 py-2.5 text-sm text-ink-900 outline-none placeholder:text-ink-400 focus:border-green-600 focus:ring-2 focus:ring-green-600/20 disabled:cursor-not-allowed disabled:opacity-50'

export const caseFormSchema = z.object({
  reference: z
    .string()
    .min(1, 'Reference is required')
    .regex(/^[a-zA-Z0-9]+$/, 'Only letters and numbers are allowed'),
  belongs_to_doctor_id: z.string().min(1, 'Consultant is required'),
  department_id: z.string().min(1, 'Department is required'),
  procedure_id: z.string().min(1, 'Procedure is required'),
  age: z
    .string()
    .min(1, 'Age is required')
    .refine((value) => {
      const age = Number(value)
      return Number.isInteger(age) && age >= 0 && age <= 150
    }, 'Age must be a whole number between 0 and 150'),
  gender: z.string().min(1, 'Gender is required'),
  procedure_date: z.string(),
  phase: z.enum(['pre_op', 'post_op']),
  operative_time: z.string(),
  is_global: z.boolean(),
  allow_national_comments: z.boolean(),
  open_for_assistant_review: z.boolean(),
  description: z.string(),
  past_medical_history: z.string(),
  note: z.string(),
  outcome: z.string(),
  comorbidity_ids: z.array(z.string()),
  complication_ids: z.array(z.string()),
})

export type CaseFormValues = z.infer<typeof caseFormSchema>

export const emptyCaseForm: CaseFormValues = {
  reference: '',
  belongs_to_doctor_id: '',
  department_id: '',
  procedure_id: '',
  age: '',
  gender: '',
  procedure_date: '',
  phase: 'pre_op',
  operative_time: '',
  is_global: false,
  allow_national_comments: false,
  open_for_assistant_review: false,
  description: '',
  past_medical_history: '',
  note: '',
  outcome: '',
  comorbidity_ids: [],
  complication_ids: [],
}

export function caseToFormValues(caseItem: Case): CaseFormValues {
  return {
    reference: caseItem.reference,
    belongs_to_doctor_id: caseItem.belongs_to_doctor_id,
    department_id: caseItem.department_id ?? '',
    procedure_id: caseItem.procedure_id ?? '',
    age: caseItem.age != null ? String(caseItem.age) : '',
    gender: caseItem.gender ?? '',
    procedure_date: caseItem.procedure_date ?? '',
    phase: caseItem.phase ?? 'pre_op',
    operative_time:
      caseItem.operative_time != null ? String(caseItem.operative_time) : '',
    is_global: caseItem.is_global,
    allow_national_comments: Boolean(caseItem.allow_national_comments),
    open_for_assistant_review: Boolean(caseItem.open_for_assistant_review),
    description: caseItem.description ?? '',
    past_medical_history: caseItem.past_medical_history ?? '',
    note: caseItem.note ?? '',
    outcome: caseItem.outcome ?? '',
    comorbidity_ids: caseItem.comorbidities?.map((item) => item.id) ?? [],
    complication_ids: caseItem.complications?.map((item) => item.id) ?? [],
  }
}

export function buildCasePayload(values: CaseFormValues): CaseFormPayload {
  return {
    reference: values.reference,
    belongs_to_doctor_id: values.belongs_to_doctor_id,
    department_id: values.department_id,
    procedure_id: values.procedure_id,
    age: Number(values.age),
    gender: values.gender as Gender,
    procedure_date: values.procedure_date || undefined,
    phase: values.phase as CasePhase,
    operative_time: values.operative_time ? Number(values.operative_time) : undefined,
    is_global: values.is_global,
    allow_national_comments: values.is_global ? values.allow_national_comments : false,
    open_for_assistant_review: values.open_for_assistant_review,
    description: values.description || undefined,
    past_medical_history: values.past_medical_history || undefined,
    note: values.note || undefined,
    outcome: values.outcome || undefined,
    comorbidity_ids: values.comorbidity_ids,
    complication_ids: values.complication_ids,
  }
}

export function useCaseForm(defaultValues: CaseFormValues = emptyCaseForm) {
  return useForm<CaseFormValues>({
    resolver: zodResolver(caseFormSchema),
    defaultValues,
  })
}

type CaseFormFieldsProps = {
  form: UseFormReturn<CaseFormValues>
  filters: CaseFiltersResponse | null
  doctorOptions: SelectOption[]
  /** Assistant / Super Admin: pick consultant doctor. */
  showDoctorPicker: boolean
  /** Doctor creating/editing own case: consultant is locked to self. */
  consultantReadOnly?: boolean
  disabled?: boolean
  /**
   * Show the free-text outcome field. Hidden at case creation; only enabled on edit
   * once the case is published and the procedure date has passed.
   */
  showOutcome?: boolean
  /** Optional slot after case description (e.g. media uploader on create). */
  afterDescription?: ReactNode
}

function toSelectOptions(items: Array<{ id: string; name: string }>): SelectOption[] {
  return items.map((item) => ({ value: item.id, label: item.name }))
}

function FieldError({ message }: { message?: string }) {
  if (!message) {
    return null
  }

  return <p className="mt-1 text-xs text-danger">{message}</p>
}

export function CaseFormFields({
  form,
  filters,
  doctorOptions,
  showDoctorPicker,
  consultantReadOnly = false,
  disabled = false,
  showOutcome = false,
  afterDescription,
}: CaseFormFieldsProps) {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = form

  const fieldsDisabled = disabled

  const departmentTree = useMemo(
    () => (filters?.departments ?? []) as Department[],
    [filters?.departments],
  )
  const procedureOptions = useMemo(
    () => toSelectOptions(filters?.procedures ?? []),
    [filters?.procedures],
  )
  const genderOptions = useMemo(
    () =>
      (filters?.genders ?? []).map((item) => ({
        value: item.value,
        label: item.label,
      })),
    [filters?.genders],
  )
  const phaseOptions = useMemo(() => {
    if (filters?.phases?.length) {
      return filters.phases.map((item) => ({
        value: item.value,
        label: item.label,
      }))
    }

    return CASE_PHASE_FILTERS.map((phase) => ({
      value: phase,
      label: CASE_PHASE_FIELD_LABELS[phase],
    }))
  }, [filters?.phases])
  const comorbidityOptions = useMemo(
    () => toSelectOptions(filters?.comorbidities ?? []),
    [filters?.comorbidities],
  )
  const complicationOptions = useMemo(
    () => toSelectOptions(filters?.complications ?? []),
    [filters?.complications],
  )

  const showConsultantColumn = showDoctorPicker

  return (
    <div className="space-y-5">
      <FormRequiredLegend />

      {/* Row: Reference + Consultant (assistant / admin) + Phase */}
      <div
        className={`grid gap-5 ${showConsultantColumn ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}
      >
        <div>
          <FieldLabel required>Reference</FieldLabel>
          <Input
            {...register('reference')}
            disabled={fieldsDisabled}
            placeholder="e.g. CASE2026001"
            autoComplete="off"
          />
          <FieldError message={errors.reference?.message} />
        </div>

        {showDoctorPicker ? (
          <div>
            <FieldLabel required>Consultant</FieldLabel>
            <SelectPicker
              options={doctorOptions}
              value={watch('belongs_to_doctor_id')}
              onChange={(value) =>
                setValue('belongs_to_doctor_id', value, { shouldValidate: true })
              }
              placeholder="Select consultant"
            />
            <FieldError message={errors.belongs_to_doctor_id?.message} />
          </div>
        ) : null}

        {/* Doctor: consultant is always the current user — keep value, hide the field. */}
        {consultantReadOnly ? (
          <input type="hidden" {...register('belongs_to_doctor_id')} />
        ) : null}

        <div>
          <FieldLabel required>Phase</FieldLabel>
          <SelectPicker
            options={phaseOptions}
            value={watch('phase')}
            onChange={(value) =>
              setValue('phase', value as CasePhase, { shouldValidate: true })
            }
            placeholder="Select phase"
          />
          <FieldError message={errors.phase?.message} />
        </div>
      </div>

      {/* Row: Department + Procedure + Procedure date */}
      <div className="grid gap-5 sm:grid-cols-3">
        <div>
          <FieldLabel required>Department</FieldLabel>
          <DepartmentPicker
            tree={departmentTree}
            value={watch('department_id')}
            onChange={(value) =>
              setValue('department_id', value, { shouldValidate: true })
            }
            required
            placeholder="Select department"
            disabled={fieldsDisabled}
          />
          <FieldError message={errors.department_id?.message} />
        </div>
        <div>
          <FieldLabel required>Procedure</FieldLabel>
          <SelectPicker
            options={procedureOptions}
            value={watch('procedure_id')}
            onChange={(value) =>
              setValue('procedure_id', value, { shouldValidate: true })
            }
            placeholder="Select procedure"
          />
          <FieldError message={errors.procedure_id?.message} />
        </div>
        <div>
          <FieldLabel optional>Procedure date</FieldLabel>
          <Input type="date" {...register('procedure_date')} disabled={fieldsDisabled} />
          <p className="mt-1.5 text-xs text-ink-500">
            Optional in draft; required before publish/schedule.
          </p>
        </div>
      </div>

      {/* Row: Age + Gender + Operative time */}
      <div className="grid gap-5 sm:grid-cols-3">
        <div>
          <FieldLabel required>Age</FieldLabel>
          <Input
            type="number"
            min={0}
            max={150}
            {...register('age')}
            disabled={fieldsDisabled}
            placeholder="Patient age"
          />
          <FieldError message={errors.age?.message} />
        </div>
        <div>
          <FieldLabel required>Gender</FieldLabel>
          <SelectPicker
            options={genderOptions}
            value={watch('gender')}
            onChange={(value) => setValue('gender', value, { shouldValidate: true })}
            placeholder="Select gender"
          />
          <FieldError message={errors.gender?.message} />
        </div>
        <div>
          <FieldLabel optional>Operative time (minutes)</FieldLabel>
          <Input
            type="number"
            min={0}
            step="0.01"
            {...register('operative_time')}
            disabled={fieldsDisabled}
            placeholder="e.g. 90"
          />
        </div>
      </div>

      <div>
        <FieldLabel optional>Case description</FieldLabel>
        <textarea
          className={fieldTextareaClassName}
          {...register('description')}
          disabled={fieldsDisabled}
          placeholder="Summarise the case…"
          rows={4}
        />
      </div>

      {afterDescription}

      {/* Row: Comorbidities + Complications */}
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <FieldLabel optional>Comorbidities</FieldLabel>
          <MultiSelectPicker
            options={comorbidityOptions}
            values={watch('comorbidity_ids')}
            onChange={(values) => setValue('comorbidity_ids', values)}
            placeholder="Select comorbidities"
          />
        </div>
        <div>
          <FieldLabel optional>Complications</FieldLabel>
          <MultiSelectPicker
            options={complicationOptions}
            values={watch('complication_ids')}
            onChange={(values) => setValue('complication_ids', values)}
            placeholder="Select complications"
          />
        </div>
      </div>

      {/* Row: Notes + Past medical history */}
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <FieldLabel optional>Notes</FieldLabel>
          <textarea
            className={fieldTextareaClassName}
            {...register('note')}
            disabled={fieldsDisabled}
            placeholder="Additional notes…"
            rows={4}
          />
        </div>
        <div>
          <FieldLabel optional>Past medical history</FieldLabel>
          <textarea
            className={fieldTextareaClassName}
            {...register('past_medical_history')}
            disabled={fieldsDisabled}
            placeholder="Relevant history…"
            rows={4}
          />
        </div>
      </div>

      {showOutcome && (
        <div>
          <FieldLabel optional>Outcome</FieldLabel>
          <textarea
            className={fieldTextareaClassName}
            {...register('outcome')}
            disabled={fieldsDisabled}
            placeholder="Describe the clinical outcome…"
            rows={4}
          />
          <p className="mt-1.5 text-xs text-ink-500">
            Free-text outcome, available now that the case is published and the procedure
            date has passed.
          </p>
        </div>
      )}
    </div>
  )
}

type CaseNationalHubFieldProps = {
  form: UseFormReturn<CaseFormValues>
  disabled?: boolean
  canSetGlobal?: boolean
}

/** Highlighted National Hub public/private toggle. */
export function CaseNationalHubField({
  form,
  disabled = false,
  canSetGlobal = true,
}: CaseNationalHubFieldProps) {
  if (!canSetGlobal) {
    return null
  }

  const isGlobal = form.watch('is_global')

  return (
    <label
      className={`flex h-full cursor-pointer items-start gap-3 rounded-xl border-2 p-4 transition-colors ${
        isGlobal
          ? 'border-green-600 bg-green-50 shadow-sm'
          : 'border-parch-200 bg-parch-50/80 hover:border-green-400 hover:bg-green-50/40'
      } ${disabled ? 'pointer-events-none opacity-60' : ''}`}
    >
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 shrink-0 accent-green-600"
        {...form.register('is_global', {
          onChange: (event) => {
            if (!event.target.checked) {
              form.setValue('allow_national_comments', false)
            }
          },
        })}
        disabled={disabled}
      />
      <span className="min-w-0">
        <span className="flex items-center gap-2 text-sm font-semibold text-ink-900">
          <Globe className="h-4 w-4 shrink-0 text-green-700" aria-hidden />
          {isGlobal ? 'Public case' : 'Private case'}
        </span>
        <span className="mt-1 block text-xs leading-relaxed text-ink-600">
          {isGlobal
            ? 'This case is public and will appear on the National Hub when it is published and not under review.'
            : 'This case is private to your team. Check the box to make it public on the National Hub.'}
        </span>
      </span>
    </label>
  )
}

type CaseAllowNationalCommentsFieldProps = {
  form: UseFormReturn<CaseFormValues>
  disabled?: boolean
}

/** Shown under Public/Private when the case is public (create + edit). */
export function CaseAllowNationalCommentsField({
  form,
  disabled = false,
}: CaseAllowNationalCommentsFieldProps) {
  const isGlobal = form.watch('is_global')

  if (!isGlobal) {
    return null
  }

  return (
    <label
      className={`flex cursor-pointer items-start gap-3 rounded-xl border border-parch-200 bg-white p-4 ${
        disabled ? 'pointer-events-none opacity-60' : ''
      }`}
    >
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 shrink-0 accent-green-600"
        {...form.register('allow_national_comments')}
        disabled={disabled}
      />
      <span className="min-w-0">
        <span className="text-sm font-semibold text-ink-900">Allow National Hub comments</span>
        <span className="mt-1 block text-xs leading-relaxed text-ink-600">
          When enabled, other doctors can post comments on this public case (with optional
          anonymity). Comments appear under National Comments on the case view.
        </span>
      </span>
    </label>
  )
}

/** Doctor-only: open this case into linked assistants' Cases for Review. */
export function CaseAssistantReviewField({
  form,
  disabled = false,
  visible = true,
}: {
  form: UseFormReturn<CaseFormValues>
  disabled?: boolean
  visible?: boolean
}) {
  if (!visible) {
    return null
  }

  const open = form.watch('open_for_assistant_review')

  return (
    <label
      className={`flex h-full cursor-pointer items-start gap-3 rounded-xl border-2 p-4 transition-colors ${
        open
          ? 'border-green-600 bg-green-50 shadow-sm'
          : 'border-parch-200 bg-parch-50/80 hover:border-green-400 hover:bg-green-50/40'
      } ${disabled ? 'pointer-events-none opacity-60' : ''}`}
    >
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 shrink-0 accent-green-600"
        {...form.register('open_for_assistant_review')}
        disabled={disabled}
      />
      <span className="min-w-0">
        <span className="flex items-center gap-2 text-sm font-semibold text-ink-900">
          <Users className="h-4 w-4 shrink-0 text-green-700" aria-hidden />
          Visible to Assistant for review
        </span>
        <span className="mt-1 block text-xs leading-relaxed text-ink-600">
          {open
            ? 'Linked assistants can view and edit this case from Cases for Review.'
            : 'Check to share this case with your linked assistants for review.'}
        </span>
      </span>
    </label>
  )
}

type CaseReviewToggleFieldProps = {
  /** Case already permanently closed — show read-only status. */
  reviewClosed: boolean
  /** User checked "Close Review" to close on save. */
  wantClose: boolean
  onWantClose: (wantClose: boolean) => void
  disabled?: boolean
  /** When false the control is hidden. */
  visible?: boolean
}

/**
 * Edit-only review control.
 * Review opens automatically when ≥1 reviewer is saved.
 * While under review, this checkbox closes it (permanent — cannot reopen).
 */
export function CaseReviewToggleField({
  reviewClosed,
  wantClose,
  onWantClose,
  disabled = false,
  visible = true,
}: CaseReviewToggleFieldProps) {
  if (!visible) {
    return null
  }

  if (reviewClosed) {
    return (
      <div className="flex h-full items-start gap-3 rounded-xl border-2 border-parch-200 bg-parch-50/80 p-4">
        <span className="min-w-0">
          <span className="flex items-center gap-2 text-sm font-semibold text-ink-900">
            <ClipboardCheck className="h-4 w-4 shrink-0 text-ink-500" aria-hidden />
            Review Closed
          </span>
          <span className="mt-1 block text-xs leading-relaxed text-ink-600">
            Review was closed for this case and cannot be reopened.
          </span>
        </span>
      </div>
    )
  }

  return (
    <label
      className={`flex h-full cursor-pointer items-start gap-3 rounded-xl border-2 p-4 transition-colors ${
        wantClose
          ? 'border-danger bg-red-50 shadow-sm'
          : 'border-green-600 bg-green-50 shadow-sm hover:border-green-700'
      } ${disabled ? 'pointer-events-none opacity-60' : ''}`}
    >
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 shrink-0 accent-green-600"
        checked={wantClose}
        onChange={(event) => onWantClose(event.target.checked)}
        disabled={disabled}
      />
      <span className="min-w-0">
        <span className="flex items-center gap-2 text-sm font-semibold text-ink-900">
          <ClipboardCheck className="h-4 w-4 shrink-0 text-green-700" aria-hidden />
          Close Review
        </span>
        <span className="mt-1 block text-xs leading-relaxed text-ink-600">
          Check this box and save to close review permanently. Reviewers become read-only
          and review cannot be reopened afterward.
        </span>
      </span>
    </label>
  )
}
